// İşletme ağacı tablosu (M16 T17, mockup 18): grup bantları + çocuk satırları, ↑↓ taşıma
// (klavye erişilebilir — DR10), pasif satır soluk, seçili satır vurgulu. v1 derinlik 2
// (grup → işletme); buildEntityTree yine de keyfi derinliği çizer (depth girintisi).

import { useEffect, useRef } from "react";
import {
  buildEntityTree,
  CLIENT_ENTITY_TYPE_LABELS,
  formatEntityValidity,
  type ClientEntity,
} from "@mahir/shared";
import { entityLeafIdsUnder } from "../../lib/client-access-draft-v2";

export interface EntityTreeProps {
  entities: ClientEntity[];
  selectedId: string | null;
  valueCounts?: Record<string, number>;
  canEdit: boolean;
  onSelect: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
}

const th: React.CSSProperties = {
  textAlign: "left",
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
  padding: "8px 12px",
  borderBottom: "1px solid var(--color-border)",
  fontWeight: 600,
};
const td: React.CSSProperties = {
  padding: "9px 12px",
  borderBottom: "1px solid var(--color-border)",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  verticalAlign: "middle",
};

export function EntityTree(props: EntityTreeProps): JSX.Element {
  const { entities, selectedId, valueCounts, canEdit, onSelect, onMove } = props;
  const tree = buildEntityTree(entities);
  const flat = tree.flatten();

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        role="treegrid"
        aria-label="İşletmeler"
        style={{ width: "100%", borderCollapse: "collapse" }}
      >
        <thead>
          <tr>
            <th style={th}>İşletme</th>
            <th style={th}>Tür</th>
            <th style={th}>Kod</th>
            <th style={th}>Ülke</th>
            <th style={{ ...th, textAlign: "right" }}>Değer sayısı</th>
            <th style={th}>Durum</th>
            <th style={{ ...th, width: 96 }}></th>
          </tr>
        </thead>
        <tbody>
          {flat.map((node) => {
            const e = node.row;
            const isGroup = node.children.length > 0 || e.type === "group";
            const selected = e.id === selectedId;
            const siblings = (
              e.parentId ? (tree.byId.get(e.parentId)?.children ?? []) : tree.roots
            ).map((n) => n.row.id);
            const idx = siblings.indexOf(e.id);
            const vc = valueCounts?.[e.id];
            const validity = formatEntityValidity(e); // TODO-KPI-11 — "2023–2025" rozeti
            return (
              <tr
                key={e.id}
                role="row"
                aria-level={node.depth + 1}
                aria-selected={selected}
                onClick={() => onSelect(e.id)}
                style={{
                  cursor: "pointer",
                  background: selected
                    ? "color-mix(in srgb, var(--color-primary) 8%, white)"
                    : isGroup
                      ? "var(--color-bg)"
                      : "transparent",
                  opacity: e.isActive ? 1 : 0.6,
                }}
              >
                <td
                  style={{
                    ...td,
                    paddingLeft: 12 + node.depth * 22,
                    fontWeight: isGroup ? 700 : 400,
                  }}
                >
                  {isGroup ? (
                    <span
                      aria-hidden="true"
                      style={{ color: "var(--color-text-3)", marginRight: 6 }}
                    >
                      ▾
                    </span>
                  ) : null}
                  <span style={{ color: isGroup ? "var(--color-primary)" : "var(--color-text)" }}>
                    {e.name}
                  </span>
                  {e.isDefault ? (
                    <span className="ml-2 text-xs" style={{ color: "var(--color-text-3)" }}>
                      (varsayılan)
                    </span>
                  ) : null}
                  {isGroup ? (
                    <span
                      className="ml-2 text-xs"
                      style={{ color: "var(--color-text-3)", fontWeight: 400 }}
                    >
                      {node.children.length} işletme
                    </span>
                  ) : null}
                  {validity ? (
                    <span
                      className="ml-2"
                      title={`Konsolideye dahil olduğu yıllar: ${validity}`}
                      style={{
                        display: "inline-block",
                        padding: "1px 8px",
                        borderRadius: 999,
                        fontSize: "0.66rem",
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                        color: "var(--color-primary)",
                      }}
                    >
                      {validity}
                    </span>
                  ) : null}
                </td>
                <td style={td}>{CLIENT_ENTITY_TYPE_LABELS[e.type]}</td>
                <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: "0.78rem" }}>
                  {e.code}
                </td>
                <td style={td}>{e.country ?? "—"}</td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {isGroup ? (
                    <span style={{ color: "var(--color-text-3)" }}>ara toplam</span>
                  ) : (
                    (vc ?? 0)
                  )}
                </td>
                <td style={td}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: 999,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      background: e.isActive
                        ? "color-mix(in srgb, var(--color-success) 14%, white)"
                        : "var(--color-bg)",
                      color: e.isActive ? "var(--color-success)" : "var(--color-text-3)",
                    }}
                  >
                    {e.isActive ? "AKTİF" : "PASİF"}
                  </span>
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }} onClick={(ev) => ev.stopPropagation()}>
                  {canEdit ? (
                    <>
                      <button
                        type="button"
                        aria-label={`${e.name} yukarı taşı`}
                        disabled={idx <= 0}
                        onClick={() => onMove(e.id, -1)}
                        style={iconBtn(idx <= 0)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        aria-label={`${e.name} aşağı taşı`}
                        disabled={idx < 0 || idx >= siblings.length - 1}
                        onClick={() => onMove(e.id, 1)}
                        style={iconBtn(idx < 0 || idx >= siblings.length - 1)}
                      >
                        ↓
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`${e.name} düzenle`}
                    onClick={() => onSelect(e.id)}
                    style={iconBtn(false)}
                  >
                    ✎
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function iconBtn(disabled: boolean): React.CSSProperties {
  return {
    minWidth: 28,
    height: 28,
    marginLeft: 2,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: disabled ? "var(--color-text-3)" : "var(--color-text-2)",
    cursor: disabled ? "default" : "pointer",
    fontSize: "0.8rem",
  };
}

// ─── Onay kutulu salt-okunur mod (portal yetki v2 §8.1 "İşletmeler") ─────────────────────────────
// Erişim çekmecesinde işletme kapsamı seçimi: grup kutusu altındaki YAPRAKLARI işaretler/kaldırır
// (kısmi seçimde indeterminate); yalnız yaprak id'leri taşınır. Düzenleme/taşıma/sütun yok.

export interface EntityCheckTreeProps {
  entities: ClientEntity[];
  checked: ReadonlySet<string>;
  disabled?: boolean | undefined;
  ariaLabel: string;
  inactiveLabel: string;
  onToggle: (leafIds: string[], on: boolean) => void;
}

function CheckBox(props: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = props.indeterminate;
  }, [props.indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={props.checked}
      disabled={props.disabled}
      onChange={(e) => props.onChange(e.target.checked)}
    />
  );
}

export function EntityCheckTree(props: EntityCheckTreeProps): JSX.Element {
  const { entities, checked, disabled = false, ariaLabel, inactiveLabel, onToggle } = props;
  const flat = buildEntityTree(entities).flatten();
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid="entity-check-tree"
      className="flex flex-col"
    >
      {flat.map((node) => {
        const e = node.row;
        const isGroup = node.children.length > 0 || e.type === "group";
        const leaves = isGroup ? entityLeafIdsUnder(entities, e.id) : [e.id];
        const on = leaves.filter((id) => checked.has(id)).length;
        const all = on === leaves.length && leaves.length > 0;
        return (
          <label
            key={e.id}
            className="flex items-center gap-2 text-sm py-0.5"
            style={{
              paddingLeft: node.depth * 18,
              fontWeight: isGroup ? 600 : 400,
              opacity: e.isActive ? 1 : 0.6,
            }}
          >
            <CheckBox
              checked={all}
              indeterminate={on > 0 && !all}
              // Çocuksuz grup işaretlenemez (yaprak değil — sunucu 422 entity_not_leaf).
              disabled={disabled || leaves.length === 0}
              onChange={(next) => onToggle(leaves, next)}
            />
            <span>{e.name}</span>
            {e.isActive ? null : (
              <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                ({inactiveLabel})
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
